import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number as USD currency (rounded to nearest dollar)
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format a number as compact USD ($1.5M / $250K), falling back to the full
 * currency string below $1,000. Use where horizontal space is tight (e.g.
 * glanceable list columns).
 */
export function formatCurrencyCompact(amount: number): string {
  // Compact form is only for non-negative amounts (the domain use case is
  // sponsor-articulated upside/cost figures). Negatives fall through to
  // `formatCurrency` so we don't produce awkward strings like "$-250K".
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) {
    const kRounded = Math.round(amount / 1_000);
    // Rounding can push the value across the million threshold (e.g. 999_500
    // rounds to 1000K); promote to millions to avoid a "$1000K" stutter.
    if (kRounded >= 1_000) {
      return `$${(amount / 1_000_000).toFixed(1)}M`;
    }
    return `$${kRounded}K`;
  }
  return formatCurrency(amount);
}

/**
 * Format a number as signed USD currency (+$X.XX or -$X.XX)
 */
export function formatSignedCurrency(amount: number): string {
  const sign = amount >= 0 ? '+' : '-';
  return `${sign}${formatCurrency(Math.abs(amount))}`;
}

/**
 * Format a USD amount as an approximate BTC equivalent
 */
export function formatBtc(usdAmount: number, btcPrice: number): string | null {
  if (!Number.isFinite(usdAmount) || !Number.isFinite(btcPrice) || btcPrice <= 0) return null;
  const btcAmount = usdAmount / btcPrice;
  if (btcAmount === 0) return '~0 BTC';
  const decimals = btcAmount >= 0.01 ? 4 : 6;
  return `~${btcAmount.toFixed(decimals)} BTC`;
}

// ============================================
// Private Period Helpers
// ============================================
// Note: Frontend uses `activatedAt: string` because API responses serialize dates as ISO strings.
// Backend (src/services/bountyValidation.ts) uses `activatedAt: Date` because Drizzle returns Date objects.
// Both implementations are intentionally typed for their respective contexts.

export const PRIVATE_PERIOD_HOURS = 24;

/** Milliseconds per hour - avoids magic numbers in time calculations */
const MILLISECONDS_PER_HOUR = 60 * 60 * 1000;

/**
 * Check if a bounty is currently in its private period (first 24h after activation)
 */
export function isInPrivatePeriod(bounty: { status: string; activatedAt: string | null }): boolean {
  if (bounty.status !== 'active' || !bounty.activatedAt) return false;
  const activatedTime = new Date(bounty.activatedAt).getTime();
  if (isNaN(activatedTime)) return false; // Invalid date = not in private period
  const privateUntilTime = activatedTime + PRIVATE_PERIOD_HOURS * MILLISECONDS_PER_HOUR;
  return Date.now() < privateUntilTime;
}

/**
 * Get the end date of the private period for a bounty.
 * Returns Invalid Date if activatedAt is not a valid date string.
 */
export function getPrivatePeriodEnd(activatedAt: string): Date {
  const activated = new Date(activatedAt);
  if (isNaN(activated.getTime())) {
    return new Date(NaN); // Return Invalid Date for invalid input
  }
  return new Date(activated.getTime() + PRIVATE_PERIOD_HOURS * MILLISECONDS_PER_HOUR);
}

// ============================================
// Time & Date Helpers
// ============================================

/**
 * Get human-readable time remaining until close date
 */
/**
 * Parse a value to a finite number, returning a fallback if NaN/Infinity.
 */
export function toFiniteNumber(value: number | string | null | undefined, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function getTimeRemaining(closeDate: string | null): string {
  if (!closeDate) return '';

  const now = new Date();
  const close = new Date(closeDate);
  const diff = close.getTime() - now.getTime();

  if (diff < 0) return 'Closed';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);

  if (weeks > 0) return `${weeks} week${weeks > 1 ? 's' : ''} left`;
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} left`;

  const hours = Math.floor(diff / (1000 * 60 * 60));
  return `${hours} hour${hours > 1 ? 's' : ''} left`;
}
